import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { platformsContainer } from '../../di/container';
import { authContainer } from '@/modules/auth/di/container';
import type { MyPlatform } from '../../domain/entities/platform.entity';

export function useMyPlatforms() {
  return useQuery({
    queryKey: ['my-platforms'],
    queryFn: () => platformsContainer.repository.getMyPlatforms(),
    retry: false,
  });
}

/**
 * Acessos de um perfil. Carregado **sob demanda**, na tela do perfil: um resumo
 * no card da listagem obrigaria a buscar os perks de todos os perfis no load.
 */
export function useProfilePerks(platformProfileId: string | undefined) {
  return useQuery({
    queryKey: ['profile-perks', platformProfileId],
    queryFn: () =>
      platformsContainer.repository.getProfilePerks(platformProfileId!),
    enabled: !!platformProfileId,
    retry: false,
  });
}

/**
 * "Ir para plataforma": pede o custom token e navega para o `/handoff` do
 * destino. Mutação, não query — não tem cache e não deve ser revalidado.
 *
 * Duas restrições que não são estilo:
 *
 * 1. **Mesma aba** (`location.href`). `window.open` disparado depois de um
 *    `await` é bloqueado como popup — o token chega e a aba não abre.
 * 2. **Fragmento** (`#ct=`), nunca query string: fragmento não é enviado ao
 *    servidor, então o token não entra em access log, `Referer` nem log de
 *    proxy. A página de destino ainda o limpa da URL antes de qualquer `await`.
 */
export function useHandoff() {
  const [pendingScopeId, setPendingScopeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const goToPlatform = async (platform: MyPlatform) => {
    setError(null);
    setPendingScopeId(platform.scopeId);
    try {
      const customToken = await authContainer.repository.createHandoffToken();
      window.location.href = `${platform.url}/handoff#ct=${encodeURIComponent(
        customToken,
      )}`;
    } catch (err) {
      console.error('Falha ao iniciar o handoff', err);
      setError(
        'Não foi possível abrir a plataforma agora. Tente novamente em instantes.',
      );
      setPendingScopeId(null);
    }
  };

  return { goToPlatform, pendingScopeId, error };
}
